import {DataTableColumn} from 'mantine-datatable';
import {NavLink} from 'react-router-dom';
import {I18n} from '@aws-amplify/core';
import {
    Badge,
    Code,
    Group,
    Stack,
    Text,
    Tooltip,
} from '@mantine/core';
import {AiOutlineEnter} from 'react-icons/ai';

import {formatTimestamp} from '../../../utils/TableTimestamps.tsx';
import {
    OutcomeBadge,
    VerificationBadge,
} from '../profile/Badges.tsx';
import {CascadeRow} from '../utils/cascadeRows.ts';
import config from '../config';

const durationLabel = (durationMs?: number | null) =>
    typeof durationMs === 'number' ? `${durationMs} ms` : '—';

// The columns render CascadeRow, not TestObservation: a row carries its depth and
// the reason it is subordinate. A dependent is indented under its root and drawn
// muted so a four-row cascade reads as one bug with three consequences instead of
// four equal failures (FNP-861).
export const useTestObservationsTableColumns = () => {

    const columns: DataTableColumn<CascadeRow>[] = [
        {
            accessor: 'observedAt',
            title: I18n.get('Observed'),
            width: '190px',
            render: (row: CascadeRow) => (
                <Text size={'sm'} c={row.muted ? 'dimmed' : undefined}>
                    {formatTimestamp(row.record.observedAt)}
                </Text>
            ),
        },
        {
            accessor: 'actionCode',
            title: I18n.get('Action'),
            width: '1fr',
            render: (row: CascadeRow) => (
                <Group gap={'xs'} wrap={'nowrap'} pl={row.depth * 24}>
                    {row.depth > 0 && (
                        // The visual "this defers to the row above" marker. Paired with
                        // the indent and the muted text, the group reads as a unit.
                        <Tooltip
                            label={`Blocked by ${row.blockedBy.join(', ')}`}
                            withArrow
                        >
                            <Text c={'dimmed'} span>
                                <AiOutlineEnter style={{transform: 'rotate(-90deg)'}}/>
                            </Text>
                        </Tooltip>
                    )}
                    <NavLink to={`${config.basePath}/${encodeURIComponent(row.record.id)}`}>
                        <Code c={row.muted ? 'dimmed' : undefined}>{row.record.actionCode}</Code>
                    </NavLink>
                </Group>
            ),
        },
        {
            accessor: 'outcome',
            title: I18n.get('Outcome'),
            width: '190px',
            render: (row: CascadeRow) => (
                <Stack gap={4}>
                    <OutcomeBadge record={row.record} kind={row.kind}/>
                    {row.childCount > 0 && (
                        // Stated on the ROOT: the blast radius of this one failure.
                        <Badge size={'xs'} variant={'outline'} color={'red'}>
                            {`blocked ${row.childCount} ${row.childCount === 1 ? 'action' : 'actions'}`}
                        </Badge>
                    )}
                </Stack>
            ),
        },
        {
            accessor: 'verificationResult',
            title: I18n.get('Verification'),
            width: '150px',
            // Equal billing with Outcome — a column of its own, never buried in the
            // detail pane. PASS + NOT_VERIFIED is the highest-value signal here.
            render: (row: CascadeRow) => <VerificationBadge verificationResult={row.record.verificationResult}/>,
        },
        {
            accessor: 'syntheticUserId',
            title: I18n.get('Synthetic User'),
            width: '1fr',
            render: (row: CascadeRow) => (
                <NavLink to={`/synthetic-users/${encodeURIComponent(row.record.syntheticUserId)}`}>
                    <Text size={'xs'} ff={'monospace'}>{row.record.syntheticUserId}</Text>
                </NavLink>
            ),
        },
        {
            accessor: 'durationMs',
            title: I18n.get('Duration'),
            width: '100px',
            render: (row: CascadeRow) => (
                <Text size={'sm'} c={'dimmed'}>{durationLabel(row.record.durationMs)}</Text>
            ),
        },
        {
            accessor: 'error',
            title: I18n.get('Detail'),
            width: '1fr',
            render: (row: CascadeRow) => {
                // An orphaned dependent (its root is not on this page) must still say
                // what it was waiting on rather than looking like a bare skip.
                if (row.depth === 0 && row.blockedBy.length > 0) {
                    return (
                        <Text size={'xs'} c={'dimmed'}>
                            {`Waiting on ${row.blockedBy.join(', ')} (not in view)`}
                        </Text>
                    );
                }
                if (!row.record.error) {
                    return <Text size={'xs'} c={'dimmed'}>—</Text>;
                }
                return (
                    <Tooltip label={row.record.error} multiline w={360} withArrow>
                        <Text size={'xs'} c={row.muted ? 'dimmed' : undefined} lineClamp={2}>
                            {row.record.error}
                        </Text>
                    </Tooltip>
                );
            },
        },
    ];

    return {
        columns,
    };
};
